/**
 * Typing Visual Effects Manager
 * 
 * Manages confetti, screen shake, and other visual effects for typing
 * with performance optimizations and customizable effects.
 */

import confetti from 'canvas-confetti';

export interface VisualEffectsConfig {
  confettiEnabled: boolean;
  confettiType: 'standard' | 'stars' | 'emojis' | 'maple-leaves' | 'snow-prism' | 'hearts';
  confettiAmount: number; // 1-10 scale
  screenShakeEnabled: boolean;
  screenShakeIntensity: number; // 0-10 scale
  rainbowTextEnabled: boolean;
  progressCelebrationEnabled: boolean;
}

export interface CaretPosition {
  x: number;
  y: number;
}

export class TypingVisualEffectsManager {
  private config: VisualEffectsConfig;
  private confettiInstance: any = null;

  constructor(config: Partial<VisualEffectsConfig> = {}) {
    this.config = {
      confettiEnabled: true,
      confettiType: 'standard',
      confettiAmount: 3,
      screenShakeEnabled: false,
      screenShakeIntensity: 2,
      rainbowTextEnabled: false,
      progressCelebrationEnabled: true,
      ...config
    };

    // Initialize confetti instance
    this.confettiInstance = confetti;
  }

  /**
   * Trigger typing effects at caret position
   */
  triggerTypingEffects(caretPosition: CaretPosition): void {
    if (this.config.confettiEnabled) {
      this.createConfetti(caretPosition);
    }

    if (this.config.screenShakeEnabled) {
      this.shakeScreen();
    }
  }

  /**
   * Create confetti effect at caret position
   */
  private createConfetti(position: CaretPosition): void {
    if (!this.confettiInstance) {
      return;
    }

    // Validate position - if invalid, use center of screen
    let validPosition = position;
    if (!position || position.x < 0 || position.y < 0 ||
        position.x > window.innerWidth || position.y > window.innerHeight) {
      validPosition = {
        x: window.innerWidth / 2,
        y: window.innerHeight / 2
      };
    }

    const particleCount = Math.max(1, this.config.confettiAmount * 2);
    const origin = {
      x: validPosition.x / window.innerWidth,
      y: validPosition.y / window.innerHeight
    };

    switch (this.config.confettiType) {
      case 'standard':
        this.confettiInstance({
          particleCount,
          startVelocity: 15,
          spread: 360,
          origin,
          colors: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57']
        });
        break;

      case 'stars':
        this.confettiInstance({
          particleCount,
          startVelocity: 20,
          spread: 360,
          origin,
          shapes: ['star'],
          colors: ['#FFE400', '#FFBD00', '#E89400', '#FFCA6C', '#FDFFB8']
        });
        break;

      case 'emojis':
        try {
          const smileyEmojis = ['😂', '😍', '🥳', '😊', '😎', '🤩'];
          const emojiShapes = smileyEmojis.map(emoji =>
            confetti.shapeFromText({ text: emoji, scalar: 3 }) // Bigger emojis
          );

          this.confettiInstance({
            particleCount,
            startVelocity: 20,
            spread: 360,
            origin,
            shapes: emojiShapes,
            flat: true, // Keep 2D, no rotation
            gravity: 0.8,
            scalar: 1.5 // Make them more vibrant and bigger
          });
        } catch (error) {
          console.warn('Emoji confetti failed, falling back to standard:', error);
          // Fallback to colorful confetti if emoji shapes fail
          this.confettiInstance({
            particleCount,
            startVelocity: 20,
            spread: 360,
            origin,
            colors: ['#FFD700', '#FFA500', '#FF69B4', '#00CED1', '#9370DB'],
            flat: true,
            gravity: 0.8
          });
        }
        break;

      case 'maple-leaves':
        try {
          const mapleLeaf = confetti.shapeFromText({ text: '🍁', scalar: 4 }); // Bigger leaves

          this.confettiInstance({
            particleCount,
            startVelocity: 20, // Same as stars for consistent animation
            spread: 360,
            origin,
            shapes: [mapleLeaf],
            colors: ['#FF4500', '#FF6347', '#FF8C00', '#FFA500', '#DC143C'], // More vibrant autumn colors
            flat: true, // Keep 2D
            gravity: 0.8, // Same as stars for consistent physics
            scalar: 2.0 // Much bigger and more prominent
          });
        } catch (error) {
          console.warn('Maple leaves confetti failed, falling back to stars:', error);
          // Fallback to star shapes with vibrant autumn colors if leaf shapes fail
          this.confettiInstance({
            particleCount,
            startVelocity: 20,
            spread: 360,
            origin,
            shapes: ['star'],
            colors: ['#FF4500', '#FF6347', '#FF8C00', '#FFA500', '#DC143C'],
            flat: true,
            gravity: 0.8,
            scalar: 2.0
          });
        }
        break;

      case 'snow-prism':
        try {
          const snowflake = confetti.shapeFromText({ text: '❄️', scalar: 4 }); // Much bigger snowflakes

          this.confettiInstance({
            particleCount,
            startVelocity: 15, // Faster like stars for more vibrant effect
            spread: 360,
            origin,
            shapes: [snowflake],
            colors: ['#0066FF', '#0080FF', '#3399FF', '#66B2FF', '#99CCFF'], // More vibrant blue variations
            flat: true, // Keep 2D
            gravity: 0.8, // Faster fall like stars for more dynamic effect
            scalar: 2.0, // Much bigger and more prominent
            drift: Math.random() * 0.5 - 0.25 // Gentle side-to-side drift
          });
        } catch (error) {
          console.warn('Snowflake confetti failed, falling back to stars:', error);
          // Fallback to star shapes with vibrant blue colors if snowflake shapes fail
          this.confettiInstance({
            particleCount,
            startVelocity: 15,
            spread: 360,
            origin,
            shapes: ['star'],
            colors: ['#0066FF', '#0080FF', '#3399FF', '#66B2FF', '#99CCFF'],
            flat: true,
            gravity: 0.8,
            scalar: 2.0,
            drift: Math.random() * 0.5 - 0.25
          });
        }
        break;

      case 'hearts':
        try {
          const heart = confetti.shapeFromText({ text: '❤️', scalar: 4 }); // Bigger hearts

          this.confettiInstance({
            particleCount,
            startVelocity: -25, // Negative velocity for upward movement
            spread: 360,
            origin,
            shapes: [heart],
            colors: ['#FF1744', '#E91E63', '#F06292', '#FF4081', '#FF80AB'], // Various shades of pink/red
            flat: true, // Keep 2D
            gravity: -0.3, // Negative gravity to make hearts float upward
            scalar: 2.0, // Bigger and more prominent
            drift: Math.random() * 0.3 - 0.15, // Gentle side-to-side drift
            ticks: 300 // Longer duration for upward floating effect
          });
        } catch (error) {
          console.warn('Hearts confetti failed, falling back to standard:', error);
          // Fallback to colorful confetti with heart-like colors if heart shapes fail
          this.confettiInstance({
            particleCount,
            startVelocity: -25,
            spread: 360,
            origin,
            colors: ['#FF1744', '#E91E63', '#F06292', '#FF4081', '#FF80AB'],
            flat: true,
            gravity: -0.3,
            scalar: 2.0,
            ticks: 300
          });
        }
        break;
    }
  }



  /**
   * Screen shake effect
   */
  private shakeScreen(): void {
    const intensity = this.config.screenShakeIntensity;
    const duration = 100;

    // Find the editor container
    const editorContainer = document.querySelector('[data-plate-editor]') as HTMLElement;
    if (!editorContainer) return;

    const originalTransform = editorContainer.style.transform;
    
    const shake = () => {
      const x = (Math.random() - 0.5) * intensity;
      const y = (Math.random() - 0.5) * intensity;
      editorContainer.style.transform = `translate(${x}px, ${y}px)`;
    };

    shake();

    setTimeout(() => {
      editorContainer.style.transform = originalTransform;
    }, duration);
  }

  /**
   * Progress celebration effect
   */
  triggerProgressCelebration(progress: number): void {
    if (!this.config.progressCelebrationEnabled) return;

    // Trigger celebration at 25%, 50%, 75%, and 100%
    const milestones = [25, 50, 75, 100];
    const milestone = milestones.find(m => 
      progress >= m && progress < m + 1 // Small range to avoid multiple triggers
    );

    if (milestone && this.confettiInstance) {
      // More dramatic effect for milestones
      this.confettiInstance({
        particleCount: milestone === 100 ? 200 : 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: milestone === 100 ? 
          ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#f9ca24'] :
          ['#4ecdc4', '#45b7d1']
      });

      // Additional side bursts for 100%
      if (milestone === 100) {
        setTimeout(() => {
          this.confettiInstance({
            particleCount: 50,
            angle: 60,
            spread: 55,
            origin: { x: 0.1, y: 0.5 }
          });
          this.confettiInstance({
            particleCount: 50,
            angle: 120,
            spread: 55,
            origin: { x: 0.9, y: 0.5 }
          });
        }, 250);
      }
    }
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<VisualEffectsConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Cleanup resources
   */
  dispose(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }

    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }

    this.particles = [];
    this.particlePool = [];
  }
}


